import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  info: ErrorInfo | null
}

/**
 * Prinde erorile de render dintr-o pagină ca să nu dărâme tot SPA-ul (ecran alb).
 * Afișează mesajul erorii + stack-ul, ca problema să poată fi diagnosticată în loc
 * să apară o pagină goală. Se remontează la schimbarea rutei (key=pathname în AppShell).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ info })
    // Lasă eroarea și în consolă pentru debugging.
    console.error('Eroare de render în pagină:', error, info)
  }

  render(): ReactNode {
    const { error, info } = this.state
    if (!error) return this.props.children

    return (
      <div className="mx-auto max-w-2xl rounded-lg border border-red-200 bg-red-50 p-6">
        <h2 className="text-lg font-semibold text-red-800">A apărut o eroare pe această pagină</h2>
        <p className="mt-1 text-sm text-red-700">
          Restul aplicației funcționează — poți naviga în altă parte. Detaliile de mai jos ajută la
          remediere.
        </p>
        <div className="mt-3 rounded border border-red-200 bg-white p-3">
          <div className="font-mono text-sm font-medium text-red-700">
            {error.name}: {error.message}
          </div>
          {(error.stack || info?.componentStack) && (
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap text-[11px] leading-snug text-b4l-text-muted">
              {error.stack}
              {info?.componentStack}
            </pre>
          )}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="mt-3 rounded bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700"
        >
          Reîncarcă pagina
        </button>
      </div>
    )
  }
}
